import { Injectable, signal, computed, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Observable, tap, catchError, throwError, switchMap, map, of, forkJoin, firstValueFrom } from 'rxjs';
import {
  Channel,
  LoginRequest,
  User,
  UserByJwtResponse,
  OAuthUrlResponse,
  OAuthExchangeRequest,
  PolicyResponse
} from '../models/auth.interface';
import { environment } from '../../../environments/environment';

export const ACTIVE_CHANNEL_KEY = 'activeChannel';
const CLIENT_KEY = 'tariff-quota';
/** Ключ, под которым identity-сервис отдаёт роли в clientRoles */
export const ROLES_CLIENT_KEY = 'agro_tariff_quota';
const SESSION_EXPIRED_KEY = 'sessionExpired';
const SESSION_CHECK_INTERVAL_MS = 2 * 60 * 1000;
const SESSION_CHECK_MIN_GAP_MS = 10 * 1000;

/** Исход GET /auth/web/me: 200 / 401 / 409 / сеть или 5xx. */
export type MeResult =
  | { status: 'authenticated'; resp: UserByJwtResponse }
  | { status: 'anonymous' | 'wrong-channel' | 'unavailable' };

/** Ошибка входа через e-Kyzmat после обмена кода (сам обмен отдаёт текст ошибки сервера). */
export class OAuthLoginError extends Error {
  constructor(public readonly reason: 'state' | 'profile' | 'wrong-channel') {
    super(`OAuth login failed: ${reason}`);
  }
}

/** Текст ошибки сервера: res[0].ru → res[0].en → msg → запасной текст. */
export function extractErrorMessage(error: unknown, fallback: string): string {
  const body = (error as HttpErrorResponse | undefined)?.error;
  const first = Array.isArray(body?.res) ? body.res[0] : null;
  return first?.ru || first?.en || body?.msg || fallback;
}

function safeStorageGet(key: string): string | null {
  try { return sessionStorage.getItem(key); } catch { return null; }
}
function safeStorageSet(key: string, value: string): void {
  try { sessionStorage.setItem(key, value); } catch { /* ignore */ }
}
function safeStorageRemove(key: string): void {
  try { sessionStorage.removeItem(key); } catch { /* ignore */ }
}

/**
 * Сессия живёт в httpOnly cookie на бэкенде: канал farmer (логин/пароль) — AS_FARMER,
 * канал staff (e-Kyzmat) — AS_STAFF. Токенов в JS нет. Какой канал использовать для запроса,
 * сообщает заголовок X-As-Channel (см. authInterceptor). В localStorage лежит только имя
 * активного канала — это не секрет и не источник истины (bootstrap переспрашивает бэкенд).
 */
@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly OAUTH_STATE_KEY = 'oauthState';

  private activeChannelSignal = signal<Channel | null>(this.readStoredChannel());
  private isAuthenticatedSignal = signal<boolean>(false);
  private currentUserSignal = signal<User | null>(null);
  private userRolesSignal = signal<string[]>([]);
  private isAssigningRoleSignal = signal<boolean>(false);
  private sessionExpiredSignal = signal<boolean>(safeStorageGet(SESSION_EXPIRED_KEY) === '1');
  private authErrorSignal = signal<string | null>(null);

  private watchStarted = false;
  private sessionChecking = false;
  private lastSessionCheck = 0;

  isAuthenticated = computed(() => this.isAuthenticatedSignal());
  currentUser = computed(() => this.currentUserSignal());
  userRoles = computed(() => this.userRolesSignal());
  isAssigningRole = computed(() => this.isAssigningRoleSignal());
  activeChannel = computed(() => this.activeChannelSignal());
  /** Сессия истекла: экран входа показывает предупреждение (флаг переживает перезагрузку). */
  sessionExpired = computed(() => this.sessionExpiredSignal());
  /** Ошибка входа, после которой пользователя вернули на экран входа (например, из callback). */
  authError = computed(() => this.authErrorSignal());

  private http = inject(HttpClient);

  /**
   * Вызывается один раз при старте приложения (provideAppInitializer): узнаёт у бэкенда
   * политику каналов и пробует /auth/web/me по каждому разрешённому. Если живых несколько,
   * берём ранее активный канал, иначе staff, иначе farmer.
   */
  bootstrap(): Promise<void> {
    return firstValueFrom(
      this.http.get<PolicyResponse>(environment.api.auth.policy, {
        params: { system: environment.authSystemId },
        withCredentials: true
      }).pipe(
        map(r => {
          const raw = r?.res?.allowedChannels;
          return (Array.isArray(raw) && raw.length > 0 ? raw : ['farmer']) as Channel[];
        }),
        catchError(() => of<Channel[]>(['farmer', 'staff'])),
        switchMap(channels =>
          forkJoin(channels.map(ch => this.probeChannel(ch).pipe(map(result => ({ ch, result })))))
        ),
        tap(results => {
          const alive = results.flatMap(x =>
            x.result.status === 'authenticated' ? [{ ch: x.ch, resp: x.result.resp }] : []
          );
          if (alive.length === 0) {
            // Сбой сети/гейтвея не означает, что сессия умерла: канал не стираем.
            const unavailable = results.some(x => x.result.status === 'unavailable');
            this.clearLocalState(!unavailable);
            return;
          }
          const stored = this.activeChannelSignal();
          const picked =
            alive.find(a => a.ch === stored) ??
            alive.find(a => a.ch === 'staff') ??
            alive[0];
          this.setActive(picked.ch, picked.resp);
        }),
        map(() => void 0),
        catchError(() => {
          this.clearLocalState(false);
          return of(void 0);
        })
      )
    );
  }

  /** GET /auth/web/me с cookie именно этого канала. 401/409 — канал не жив. */
  private fetchChannelUser(channel: Channel): Observable<UserByJwtResponse> {
    return this.http.get<UserByJwtResponse>(environment.api.auth.me, {
      params: { system: environment.authSystemId },
      headers: new HttpHeaders({ 'X-As-Channel': channel }),
      withCredentials: true
    });
  }

  /** fetchChannelUser, превращающий ошибки в исходы: сеть и 5xx — unavailable, а не anonymous. */
  private probeChannel(channel: Channel): Observable<MeResult> {
    return this.fetchChannelUser(channel).pipe(
      map((resp): MeResult => ({ status: 'authenticated', resp })),
      catchError((error: HttpErrorResponse): Observable<MeResult> => {
        const status = error?.status;
        if (status === 409) return of({ status: 'wrong-channel' });
        if (!status || status >= 500) return of({ status: 'unavailable' });
        return of({ status: 'anonymous' });
      })
    );
  }

  /** Профиль текущего активного канала (обновляет роли после assign-role и т.п.). */
  getUserByJWT(): Observable<UserByJwtResponse> {
    const channel = this.activeChannelSignal();
    if (!channel) {
      return throwError(() => new Error('No active channel'));
    }
    return this.fetchChannelUser(channel).pipe(
      tap(resp => this.setActive(channel, resp)),
      catchError(error => {
        console.error('Error getting current user:', error);
        return throwError(() => error);
      })
    );
  }

  getOAuthUrl(): Observable<OAuthUrlResponse> {
    const url = `${environment.api.ekyzmat.getOAuthUrl}?client=${CLIENT_KEY}`;

    return this.http.get<OAuthUrlResponse>(url, {
      headers: new HttpHeaders({ 'X-As-Channel': 'staff' }),
      withCredentials: true
    }).pipe(
      tap(response => {
        if (response.authorization_url) {
          const authUrl = new URL(response.authorization_url);
          const state = authUrl.searchParams.get('state');
          if (state) {
            safeStorageSet(this.OAUTH_STATE_KEY, state);
          }
        }
      })
    );
  }

  /**
   * Вход сотрудника через e-Kyzmat: обмен code на cookie-сессию канала staff, затем обязательный
   * GET /me. Если профиль не получен — это ошибка входа (OAuthLoginError), а не успех.
   */
  oauthLogin(code: string, state: string): Observable<UserByJwtResponse> {
    const savedState = safeStorageGet(this.OAUTH_STATE_KEY);
    safeStorageRemove(this.OAUTH_STATE_KEY);

    if (savedState !== state) {
      return throwError(() => new OAuthLoginError('state'));
    }

    const request: OAuthExchangeRequest = { code, state, client: CLIENT_KEY };

    return this.http.post(environment.api.auth.staffExchange, request, {
      headers: new HttpHeaders({ 'X-As-Channel': 'staff' }),
      withCredentials: true
    }).pipe(
      switchMap(() => this.probeChannel('staff')),
      map(result => {
        if (result.status !== 'authenticated') {
          this.clearLocalState();
          throw new OAuthLoginError(result.status === 'wrong-channel' ? 'wrong-channel' : 'profile');
        }
        this.setActive('staff', result.resp);
        return result.resp;
      })
    );
  }

  /** Вход поставщика по логину/паролю: бэкенд ставит httpOnly cookie AS_FARMER. */
  login(credentials: LoginRequest): Observable<UserByJwtResponse> {
    return this.http.post(environment.api.auth.farmerLogin, credentials, { withCredentials: true }).pipe(
      switchMap(() => this.fetchChannelUser('farmer')),
      tap(resp => this.setActive('farmer', resp)),
      catchError(error => {
        console.error('Login error:', error);
        return throwError(() => error);
      })
    );
  }

  /** Бэкенд сам гасит cookie; сетевая ошибка не должна блокировать локальный выход. */
  logout(): void {
    const channel = this.activeChannelSignal();
    this.http.post(environment.api.auth.logout, null, {
      params: channel ? { channel } : {},
      withCredentials: true
    }).pipe(catchError(() => of(null))).subscribe(() => {
      this.clearLocalState();
      // Перезагружаем страницу для полного сброса состояния
      window.location.href = '/';
    });
  }

  hasRole(role: string): boolean {
    return this.userRolesSignal().includes(role);
  }

  /**
   * Проверяет, нужна ли пользователю роль quota_supplier
   * Возвращает true если у пользователя нет роли quota_supplier и нет админских ролей
   */
  needsRoleAssignment(): boolean {
    const roles = this.userRolesSignal();
    const adminRoles = ['quota_manager', 'quota_operator', 'quota_admin', 'quota_viewer'];
    const hasAdminRole = adminRoles.some(role => roles.includes(role));
    const hasSupplierRole = roles.includes('quota_supplier');

    return !(hasAdminRole || hasSupplierRole);
  }

  /** Присваивает роль quota_supplier в активном канале; сессия остаётся в cookie. */
  assignRole(password: string): Observable<UserByJwtResponse> {
    const channel = this.activeChannelSignal();

    if (!channel) {
      return throwError(() => new Error('No active channel'));
    }

    this.isAssigningRoleSignal.set(true);

    const body = {
      clientKey: 'agro_tariff_quota$supplier',
      channel,
      password
    };

    return this.http.post(environment.api.auth.assignRole, body, {
      headers: new HttpHeaders({ 'X-As-Channel': channel }),
      withCredentials: true
    }).pipe(
      switchMap(() => this.fetchChannelUser(channel)),
      tap(resp => {
        this.setActive(channel, resp);
        this.isAssigningRoleSignal.set(false);
      }),
      catchError(error => {
        console.error('Error assigning role:', error);
        this.isAssigningRoleSignal.set(false);
        return throwError(() => error);
      })
    );
  }

  /** Вызывается интерцептором при 401: сессия истекла, чистим состояние без запроса к бэкенду. */
  handleSessionExpired(): void {
    this.clearLocalState();
    this.sessionExpiredSignal.set(true);
    safeStorageSet(SESSION_EXPIRED_KEY, '1');
    window.location.href = '/login';
  }

  /** Экран входа забирает флаг один раз, чтобы предупреждение не висело вечно. */
  consumeSessionExpired(): boolean {
    const v = this.sessionExpiredSignal();
    this.sessionExpiredSignal.set(false);
    safeStorageRemove(SESSION_EXPIRED_KEY);
    return v;
  }

  setAuthError(message: string | null): void {
    this.authErrorSignal.set(message);
  }

  /**
   * Сторож сессии: пока пользователь вошёл, раз в 2 минуты (видимая вкладка), на focus и на
   * visibilitychange переспрашивает /me. Не чаще раза в 10 секунд; сбой сети сессию не гасит.
   */
  private startSessionWatch(): void {
    if (this.watchStarted || typeof window === 'undefined') return;
    this.watchStarted = true;
    setInterval(() => this.checkSession(), SESSION_CHECK_INTERVAL_MS);
    window.addEventListener('focus', () => this.checkSession());
    document.addEventListener('visibilitychange', () => this.checkSession());
  }

  private checkSession(): void {
    const channel = this.activeChannelSignal();
    if (!channel || !this.isAuthenticatedSignal() || document.hidden || this.sessionChecking) return;
    if (Date.now() - this.lastSessionCheck < SESSION_CHECK_MIN_GAP_MS) return;

    this.sessionChecking = true;
    this.lastSessionCheck = Date.now();
    this.probeChannel(channel).subscribe(result => {
      this.sessionChecking = false;
      if (result.status === 'anonymous' || result.status === 'wrong-channel') {
        this.handleSessionExpired();
      }
    });
  }

  private setActive(channel: Channel, response: UserByJwtResponse): void {
    const r = response.res;
    const user: User = {
      userId: r.userId,
      username: r.username,
      email: r.email,
      name: r.name,
      givenName: r.givenName,
      familyName: r.familyName,
      phoneNumber: r.phoneNumber,
      pin: r.pin,
      realmRoles: r.realmRoles,
      clientRoles: r.clientRoles,
      roles: r.roles
    };
    this.activeChannelSignal.set(channel);
    this.currentUserSignal.set(user);
    this.userRolesSignal.set(user.clientRoles?.[ROLES_CLIENT_KEY] || []);
    this.isAuthenticatedSignal.set(true);
    try {
      localStorage.setItem(ACTIVE_CHANNEL_KEY, channel);
    } catch { /* ignore */ }
    this.startSessionWatch();
  }

  /** clearChannel=false: профиль сбрасываем, а канал оставляем (сервер был недоступен). */
  private clearLocalState(clearChannel = true): void {
    this.currentUserSignal.set(null);
    this.userRolesSignal.set([]);
    this.isAuthenticatedSignal.set(false);
    if (!clearChannel) return;
    this.activeChannelSignal.set(null);
    try {
      localStorage.removeItem(ACTIVE_CHANNEL_KEY);
    } catch { /* ignore */ }
  }

  private readStoredChannel(): Channel | null {
    try {
      const v = localStorage.getItem(ACTIVE_CHANNEL_KEY);
      return v === 'farmer' || v === 'staff' ? v : null;
    } catch {
      return null;
    }
  }
}
